import {
  BRIEFING_OCR_CONFIDENCE_WARNING,
  type Briefing,
  type BriefingSourceFile,
  briefingModelLabel,
} from '@acasa/shared'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { PageShell } from '@/components/page-shell'
import { BriefingProgress } from '@/features/briefings/briefing-progress'
import { BriefingView } from '@/features/briefings/briefing-view'
import { CreateBriefingDialog, estateChoiceOf } from '@/features/briefings/create-dialog'
import {
  briefingName,
  briefingStand,
  fileLabel,
  formatBytes,
  formatDate,
  formatDateTime,
} from '@/features/briefings/format'
import { BriefingStatusChip } from '@/features/briefings/status-chip'
import {
  briefingPdfUrl,
  isBusy,
  useBriefing,
  useBriefingFreshness,
  useBriefings,
  useDeleteBriefing,
  useLinkBriefingToOnOffice,
} from '@/features/briefings/use-briefings'
import { ApiRequestError } from '@/lib/api'

const secondaryButton =
  'border-line text-muted hover:text-ink bg-surface border px-3 py-2 text-xs transition-colors disabled:opacity-40'

/**
 * How much of a document was read by OCR, and whether to trust it. Only the
 * Mistral pipeline fills these; the other providers leave both null.
 */
function OcrNote({ file }: { file: BriefingSourceFile }) {
  const pages = file.ocrPages
  if (pages === null || pages <= 0) return null
  const confidence = file.ocrMinConfidence
  const low = confidence !== null && confidence < BRIEFING_OCR_CONFIDENCE_WARNING
  return (
    <span className="w-full text-[11px]">
      <span className="text-faint block">
        {pages} {pages === 1 ? 'Seite' : 'Seiten'} per OCR gelesen
      </span>
      {low && (
        // The warning token marks the line; the sentence itself is ink, because
        // small text in the warning tone is too faint to read on the light surface.
        <span className="border-warning text-ink mt-0.5 block border-l-2 pl-1.5">
          <span aria-hidden="true" className="text-warning mr-1">
            ⚠
          </span>
          {/* floor, not round: 0.795 must not print as the 80 % threshold */}
          Erkennungssicherheit niedrig ({Math.floor(confidence * 100)} %) — gescannte Seiten könnten
          falsch gelesen sein.
        </span>
      )}
    </span>
  )
}

/** The documents the run read, as they were then — and what onOffice has that is newer. */
function SourcesCard({ briefing }: { briefing: Briefing }) {
  const freshness = useBriefingFreshness(
    briefing.id,
    briefing.status === 'succeeded' && briefing.estate.estateId !== null,
  )
  const newer = freshness.data?.newerFiles ?? []

  return (
    <section className="border-line bg-surface mt-6 border p-4">
      <h2 className="text-faint border-line mb-3 border-b pb-2 text-[10px] font-medium tracking-[0.14em] uppercase">
        Grundlage · {briefing.sourceFiles.length}{' '}
        {briefing.sourceFiles.length === 1 ? 'Dokument' : 'Dokumente'}
      </h2>

      {newer.length > 0 && (
        <div className="border-warning/40 bg-warning/10 mb-3 border px-3 py-2 text-xs">
          <p className="text-ink font-medium">Neuere Unterlagen in onOffice:</p>
          <ul className="text-muted mt-1 space-y-0.5">
            {newer.map((file) => (
              <li key={file.id}>
                {fileLabel(file)} · {formatDate(file.modifiedAt)}
              </li>
            ))}
          </ul>
        </div>
      )}
      {freshness.isError && (
        <p className="text-faint mb-3 text-[11px]">
          Ob es neuere Unterlagen gibt, ließ sich nicht prüfen — onOffice nicht erreichbar.
        </p>
      )}

      <ul className="divide-line divide-y">
        {briefing.sourceFiles.map((file) => (
          <li
            key={file.id}
            className="flex flex-wrap items-baseline justify-between gap-x-4 py-1.5"
          >
            <span className="text-ink min-w-0 text-xs break-words">{fileLabel(file)}</span>
            <span className="text-faint tnum text-[11px]">
              {[
                formatBytes(file.sizeBytes),
                formatDate(file.modifiedAt),
                file.pages !== null
                  ? `${file.pages} ${file.pages === 1 ? 'Seite' : 'Seiten'}`
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
            <OcrNote file={file} />
          </li>
        ))}
      </ul>

      {briefing.specialQuestions && (
        <p className="text-muted border-line mt-3 border-t pt-3 text-xs">
          <span className="text-faint mr-1.5 text-[10px] tracking-wider uppercase">
            Sonderfragen
          </span>
          {briefing.specialQuestions}
        </p>
      )}
    </section>
  )
}

/**
 * Why linking failed, in words an agent can act on. The server's 409/502/503
 * messages are fixed German sentences written for exactly this; anything else
 * (a network blip, a 500) gets a generic line rather than a stack's wording.
 */
function linkErrorMessage(error: unknown): string {
  if (error instanceof ApiRequestError && [409, 502, 503].includes(error.status)) {
    return error.message
  }
  return 'Verknüpfung fehlgeschlagen.'
}

/**
 * The link written into the object in onOffice, under the buttons. Only when
 * the server can link at all and the briefing is finished — otherwise nothing,
 * so a deployment without the field configured looks as it did before.
 */
function OnOfficeLinkNote({ briefing }: { briefing: Briefing }) {
  const { linkedAt, linkedBy, current } = briefing.onOfficeLink
  if (!linkedAt) return null
  return (
    <p className="text-faint mt-2 text-[11px]">
      In onOffice verlinkt am {formatDateTime(linkedAt)}
      {linkedBy && <> von {linkedBy.name}</>}
      {!current && ' · inzwischen durch ein neueres Briefing ersetzt'}
    </p>
  )
}

export function BriefingPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data: briefing, isPending, isError, error } = useBriefing(id)
  // Only for the two flags the buttons need; the list is usually cached already.
  const list = useBriefings()
  const remove = useDeleteBriefing()
  const linkToOnOffice = useLinkBriefingToOnOffice()
  const [rerunning, setRerunning] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  if (isPending) {
    return (
      <PageShell>
        <p className="text-muted py-12 text-center text-sm">Wird geladen …</p>
      </PageShell>
    )
  }

  if (isError || !briefing) {
    return (
      <PageShell>
        <p className="border-danger/30 bg-danger/5 text-danger border px-3 py-2 text-xs">
          {error instanceof Error ? error.message : 'Briefing nicht gefunden'}
        </p>
        <Link to="/briefings" className="text-brand mt-4 inline-block text-xs underline">
          Zur Übersicht
        </Link>
      </PageShell>
    )
  }

  const busy = isBusy(briefing)
  const estate = estateChoiceOf(briefing)
  const canCreate =
    estate !== null && (list.data?.configured ?? true) && !(list.data?.limitReached ?? false)
  const rerunTitle =
    estate === null
      ? 'Das Objekt hat keine onOffice-ID'
      : list.data?.limitReached
        ? 'Monatslimit für KI-Nutzung erreicht'
        : list.data?.configured === false
          ? 'Objekt-Briefings sind nicht konfiguriert'
          : undefined

  /*
   * Back to the UNFILTERED list. Returning to `?estate=<id>` left the object
   * filter chip standing next to the search box — over a list that, after the
   * last briefing of that object was deleted, was empty — and nobody had asked
   * for that filter. The chip is for arriving FROM an object's Unterlagen row.
   */
  const doDelete = () => remove.mutate(briefing.id, { onSuccess: () => navigate('/briefings') })

  return (
    <PageShell>
      <Link to="/briefings" className="text-faint hover:text-ink text-[11px]">
        ← Alle Briefings
      </Link>

      <header className="border-line mt-3 mb-6 border-b pb-5">
        <span className="bg-brand mb-3 block h-[3px] w-8" aria-hidden />
        <div className="flex flex-wrap items-baseline gap-2">
          <h1 className="font-display text-ink text-[22px] leading-tight">
            {briefingName(briefing)}
          </h1>
          <BriefingStatusChip status={briefing.status} />
        </div>
        <p className="text-muted mt-2 flex flex-wrap gap-x-3 text-xs">
          {briefing.estate.externalNumber && (
            <span className="font-mono">{briefing.estate.externalNumber}</span>
          )}
          <span>Stand {briefingStand(briefing)}</span>
          {briefing.createdBy && <span>von {briefing.createdBy.name}</span>}
          {briefing.trigger === 'scheduled' && <span>automatisch erstellt</span>}
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {briefing.status === 'succeeded' && (
            <a
              href={briefingPdfUrl(briefing.id)}
              download
              className="bg-brand text-surface hover:bg-brand-strong px-3 py-2 text-xs font-medium transition-colors"
            >
              PDF herunterladen
            </a>
          )}
          {briefing.status === 'succeeded' && briefing.onOfficeLink.available && (
            <button
              type="button"
              disabled={linkToOnOffice.isPending}
              onClick={() => linkToOnOffice.mutate(briefing.id)}
              className={secondaryButton}
            >
              {linkToOnOffice.isPending
                ? 'wird verlinkt …'
                : briefing.onOfficeLink.linkedAt
                  ? 'Verknüpfung erneuern'
                  : 'In onOffice verlinken'}
            </button>
          )}
          {/* A failed run offers its retry in the error box instead. */}
          {briefing.status !== 'failed' && (
            <button
              type="button"
              disabled={!canCreate}
              title={rerunTitle}
              onClick={() => setRerunning(true)}
              className={secondaryButton}
            >
              Erneut erstellen
            </button>
          )}

          {/* A run in progress cannot be deleted (the API answers 409): its
              estimate still counts against the month's limit. */}
          {busy ? null : confirmingDelete ? (
            <span className="border-danger/40 bg-danger/5 inline-flex items-center gap-2 border px-2 py-1 text-xs">
              <span className="text-ink">Briefing löschen?</span>
              <button
                type="button"
                disabled={remove.isPending}
                onClick={doDelete}
                className="text-danger font-medium underline underline-offset-2 disabled:opacity-40"
              >
                {remove.isPending ? 'wird gelöscht …' : 'Ja, löschen'}
              </button>
              <button
                type="button"
                onClick={() => setConfirmingDelete(false)}
                className="text-muted hover:text-ink"
              >
                Abbrechen
              </button>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              className="text-faint hover:text-danger px-1 text-xs underline underline-offset-2"
            >
              Löschen
            </button>
          )}
        </div>
        {briefing.status === 'succeeded' && briefing.onOfficeLink.available && (
          <OnOfficeLinkNote briefing={briefing} />
        )}
        {/* Says WHY there is no button, or the feature looks missing rather than off. */}
        {briefing.status === 'succeeded' && !briefing.onOfficeLink.available && (
          <p className="text-muted mt-2 text-xs">
            Verknüpfung mit onOffice ist auf dem Server nicht eingerichtet.
          </p>
        )}
        {/* Scoped to this briefing: the page component survives navigating to
            another one, and its mutation state with it. */}
        {linkToOnOffice.isError && linkToOnOffice.variables === briefing.id && (
          <p className="text-danger mt-2 text-xs">{linkErrorMessage(linkToOnOffice.error)}</p>
        )}
        {remove.isError && (
          <p className="text-danger mt-2 text-xs">
            {remove.error instanceof Error ? remove.error.message : 'Löschen fehlgeschlagen'}
          </p>
        )}
      </header>

      {busy && <BriefingProgress briefing={briefing} />}

      {briefing.status === 'failed' && (
        <div className="border-danger/30 bg-danger/5 border px-4 py-3">
          <p className="text-danger text-sm font-medium">Das Briefing ist fehlgeschlagen.</p>
          {briefing.error && (
            <p className="text-ink mt-1 text-xs leading-relaxed break-words">{briefing.error}</p>
          )}
          <button
            type="button"
            disabled={!canCreate}
            title={rerunTitle}
            onClick={() => setRerunning(true)}
            className={`${secondaryButton} mt-3`}
          >
            Erneut versuchen
          </button>
        </div>
      )}

      {briefing.status === 'succeeded' && briefing.body && (
        <BriefingView briefing={briefing} body={briefing.body} showTitle={false} />
      )}

      <SourcesCard briefing={briefing} />

      <p className="text-faint mt-4 text-[11px]">
        Modell {briefingModelLabel({ provider: briefing.provider, id: briefing.model })} ·
        Prompt-Version {briefing.promptVersion} · angefordert {formatDateTime(briefing.createdAt)}
      </p>

      {rerunning && estate && (
        <CreateBriefingDialog
          estate={estate}
          initialFileIds={briefing.sourceFiles.map((file) => file.id)}
          initialSpecialQuestions={briefing.specialQuestions}
          onClose={() => setRerunning(false)}
        />
      )}
    </PageShell>
  )
}
